import { useCallback, useEffect, useMemo, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useFavouriteProjects } from '@/hooks/use-favourite-projects';
import { useProjects } from '@/hooks/use-projects';
import { logout } from '@/modules/auth';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const mainNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    label: 'Projects',
    path: '/projects',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
  },
];

const bottomNavItems: NavItem[] = [
  {
    label: 'Settings',
    path: '/settings',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const projectAdminNavItem: NavItem = {
  label: 'Admin',
  path: '/admin/projects',
  icon: (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
};

const playbookDefinitionsNavItem: NavItem = {
  label: 'Playbooks',
  path: '/admin/playbook-definitions',
  icon: (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
};

/**
 * Determines the active navigation item using longest prefix match.
 * For the root path "/", it only matches exactly "/".
 * For other paths, it matches the longest prefix.
 */
export function getActiveNavItem(currentPath: string, items: NavItem[]): NavItem | null {
  let bestMatch: NavItem | null = null;
  let bestLength = 0;

  for (const item of items) {
    if (item.path === '/') {
      if (currentPath === '/') {
        if (bestLength === 0) {
          bestMatch = item;
          bestLength = 1;
        }
      }
    } else {
      if (
        currentPath === item.path ||
        currentPath.startsWith(item.path + '/')
      ) {
        if (item.path.length > bestLength) {
          bestMatch = item;
          bestLength = item.path.length;
        }
      }
    }
  }

  return bestMatch;
}

/**
 * Formats the notification badge display value.
 * Returns null when count is 0 (badge hidden), numeric string for 1-99, "99+" for >99.
 */
export function formatBadgeCount(count: number): string | null {
  if (count <= 0) return null;
  if (count > 99) return '99+';
  return String(count);
}

export function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar, theme, setTheme } = useUIStore();
  const user = useAuthStore((state) => state.user);
  const { favourites } = useFavouriteProjects();
  const { data: projectsData } = useProjects();

  const isAdmin = user?.role === 'Admin';

  // Resolve favourite project keys to display names
  const favouriteProjects = useMemo(() => {
    if (!projectsData?.projects || favourites.length === 0) return [];
    return favourites
      .map((key) => projectsData.projects.find((p) => p.projectKey === key))
      .filter((p): p is NonNullable<typeof p> => p != null);
  }, [favourites, projectsData?.projects]);

  const computedBottomNavItems = useMemo(() => {
    if (isAdmin) {
      return [projectAdminNavItem, playbookDefinitionsNavItem, ...bottomNavItems];
    }
    return bottomNavItems;
  }, [isAdmin]);

  const allNavItems = useMemo(
    () => [...mainNavItems, ...computedBottomNavItems],
    [computedBottomNavItems],
  );

  const activeItem = getActiveNavItem(location.pathname, allNavItems);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/auth/callback');
  }, [navigate]);

  const handleNavClick = useCallback(() => {
    if (window.innerWidth < 1024 && !sidebarCollapsed) {
      toggleSidebar();
    }
  }, [sidebarCollapsed, toggleSidebar]);

  // Determine effective dark mode for the toggle
  const [isDark, setIsDark] = useState(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return theme === 'dark';
  });

  useEffect(() => {
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      setIsDark(mq.matches);
      const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    setIsDark(theme === 'dark');
  }, [theme]);

  const handleThemeToggle = useCallback(() => {
    setTheme(isDark ? 'light' : 'dark');
  }, [isDark, setTheme]);

  return (
    <>
      {/* Mobile hamburger button - visible below 1024px */}
      <button
        type="button"
        className="fixed top-4 left-2 z-50 lg:hidden rounded-md p-2 text-content bg-surface shadow-md hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600"
        onClick={toggleSidebar}
        aria-label={sidebarCollapsed ? 'Open navigation menu' : 'Close navigation menu'}
        aria-expanded={!sidebarCollapsed}
        aria-controls="nav-sidebar"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          {sidebarCollapsed ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          )}
        </svg>
      </button>

      {/* Backdrop overlay for mobile drawer */}
      {!sidebarCollapsed && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
          data-testid="nav-backdrop"
        />
      )}

      {/* Sidebar navigation ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â compact icon-focused layout */}
      <nav
        id="nav-sidebar"
        aria-label="Main navigation"
        className={`
          fixed top-0 left-0 z-40 h-full w-16 bg-sidebar border-r border-border 
          transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:sticky lg:top-0 lg:z-auto lg:h-full lg:self-start
          ${sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}
        `}
      >
        <div className="flex h-full flex-col items-center">
          {/* Main navigation links */}
          <ul className="flex-1 w-full pt-14 lg:pt-3 pb-3 space-y-1" role="list">
            {mainNavItems.map((item) => {
              const isActive = activeItem?.path === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={handleNavClick}
                    className={`
                      flex flex-col items-center justify-center gap-0.5 rounded-md mx-1 px-1 py-2
                      min-h-[52px]
                      transition-colors duration-150
                      focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1
                      ${
                        isActive
                          ? 'bg-sidebar-active text-sidebar-active-text'
                          : 'text-content-secondary hover:bg-brand-50 hover:text-brand-800 dark:hover:bg-brand-950 dark:hover:text-brand-200'
                      }
                    `}
                  >
                    {item.icon}
                    <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                  </Link>
                </li>
              );
            })}

            {/* Favourite projects listed under Projects */}
            {favouriteProjects.length > 0 && (
              <li>
                <ul className="w-full pt-1 space-y-0.5" role="list" aria-label="Favourite projects">
                  {favouriteProjects.map((project) => {
                    const projectPath = `/projects/${encodeURIComponent(project.projectKey)}`;
                    const isProjectActive = location.pathname === projectPath || location.pathname.startsWith(projectPath + '/');
                    return (
                      <li key={project.projectKey}>
                        <Link
                          to={projectPath}
                          onClick={handleNavClick}
                          aria-label={project.displayName}
                          title={project.displayName}
                          className={`
                            flex flex-col items-center justify-center gap-0.5 rounded-md mx-1 px-1 py-1.5
                            min-h-[40px]
                            transition-colors duration-150
                            focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1
                            ${
                              isProjectActive
                                ? 'bg-sidebar-active text-sidebar-active-text'
                                : 'text-content-secondary hover:bg-brand-50 hover:text-brand-800 dark:hover:bg-brand-950 dark:hover:text-brand-200'
                            }
                          `}
                        >
                          <svg className="h-4 w-4 text-yellow-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          <span className="text-[9px] font-medium leading-tight truncate max-w-[54px] text-center">
                            {project.projectKey}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}
          </ul>

          {/* Bottom section: Settings + Logout */}
          <div className="border-t border-border w-full py-3 space-y-1">

            {computedBottomNavItems.map((item) => {
              const isActive = activeItem?.path === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={handleNavClick}
                  className={`
                    flex flex-col items-center justify-center gap-0.5 rounded-md mx-1 px-1 py-2
                    min-h-[52px]
                    transition-colors duration-150
                    focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1
                    ${
                      isActive
                        ? 'bg-sidebar-active text-sidebar-active-text'
                        : 'text-content-secondary hover:bg-brand-50 hover:text-brand-800 dark:hover:bg-brand-950 dark:hover:text-brand-200'
                    }
                  `}
                >
                  {item.icon}
                  <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Dark/Light mode toggle */}
            <div className="flex flex-col items-center justify-center mx-1 px-1 py-2">
              <button
                type="button"
                role="switch"
                aria-checked={isDark}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                onClick={handleThemeToggle}
                className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 bg-gray-300 dark:bg-brand-600"
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isDark ? 'translate-x-4' : 'translate-x-0'}`}>
                  {isDark ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                    </svg>
                    ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                    </svg>
                  )}
                </span>
              </button>
              {/* <span className="mt-1 text-content-secondary">
                {isDark ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                )}
              </span> */}
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full flex-col items-center justify-center gap-0.5 rounded-md mx-1 px-1 py-2 min-h-[52px] text-content-secondary hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950 dark:hover:text-red-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-[10px] font-medium leading-tight">Logout</span>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}